import { Component, Input, ChangeDetectorRef, AfterViewInit, Output, EventEmitter } from '@angular/core';
import { ISorting, FormBuilderHelper } from '../../../shared/helper';
import { distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { BuilderField } from '../../models/builder.model';
import { BuilderService } from '../../../dnd/services/builder.service';

@Component({
  selector: 'fb-controls-sidebar',
  templateUrl: './controls-sidebar.component.html',
  styleUrls: ['./controls-sidebar.component.scss'],
})
export class ControlsSidebarComponent implements AfterViewInit {
  typeaheads: any[] = [];
  active = 'basic';
  sortings: ISorting[] = [];
  basicControlTypes: any[] = [];
  customControlTypes: any[] = [];
  templates: any[] = [];

  private _templateControlTypes: any[] = [];

  @Input('activeTab')
  get activeTab() {
    return this.active;
  }
  set activeTab(active) {
    this.active = active;
    this.activeTabChanged.emit(active);
  }
  @Output('activeTabChanged') activeTabChanged = new EventEmitter<any>();

  @Input('templates')
  set templateControlTypes(list) {
    this._templateControlTypes = Object.keys(list || {});
    this.templates = list;
    this.reloadTemplates();
  }
  get templateControlTypes() {
    return this._templateControlTypes;
  }

  private _formBuilderHelper = new FormBuilderHelper();

  private _filterEvent = new Subject<any>();
  private _filter: string;
  private _basicFields;
  private _customFields;
  private _renderedList = {};
  private _downloadedTemplates = {};

  private _controlProperties: any[] = [];
  private _templatesChangelog = {};

  private _model: any[] = [];
  @Input() set model(model: any) {
    if (model && JSON.stringify(model) !== JSON.stringify(this._model)) {
      this._model = model;
      this.customFields = model;
    }
  }

  @Output('onTemplateDeleted') templateDeleteEvent = new EventEmitter<any>();

  constructor(private ref: ChangeDetectorRef, private builderService: BuilderService) {
    this._filterEvent
      .pipe(distinctUntilChanged())
      .pipe(debounceTime(150))
      .subscribe(value => {
        if (value.length !== 0 && value.length < 3) {
          return;
        }
        this._filter = value;
        this.updateSortings();
      });
  }

  ngAfterViewInit() {
    this.initSortings();
    this.initBasicFields();
    this.initCustomFields();
  }

  get model() {
    return this._model;
  }

  get filter() {
    return this._filter;
  }

  get controlProperties() {
    return this._controlProperties;
  }

  set filter(value: string) {
    this._filterEvent.next(value);
  }

  get downloadedTemplates() {
    return this._downloadedTemplates;
  }

  templateDelete(template, type) {
    this.templateDeleteEvent.emit({ type, template });
  }

  toggleList(type) {
    this._renderedList[type] = !this._renderedList[type];
  }

  renderList(type) {
    return !!this._renderedList[type];
  }

  readyToRenderTemplates(type) {
    return Boolean(this._downloadedTemplates[type]);
  }

  loadingTemplate(type) {
    return this._downloadedTemplates[type] === null;
  }

  renderTemplates(type, status) {
    this._templatesChangelog[type] = status;
    this._downloadedTemplates[type] = null;
    const promises = this.templates[type].map(template => {
      return this.builderService.extractTemplate(type, template);
    });
    Promise.all(promises).then(templates => {
      this._downloadedTemplates[type] = templates;
    });
  }

  private set basicFields(value: any[]) {
    this._basicFields = this._formBuilderHelper.sanitizeFields(value, this.filter, this.sortings);
    this.basicControlTypes = Array.from(
      new Set(this._basicFields.map((field: BuilderField) => (field.designCategory || field.fieldType || '').toString())),
    );
  }

  private set customFields(value: any[]) {
    this._customFields = this._formBuilderHelper.sanitizeFields(value, this.filter, this.sortings);
    this.customControlTypes = Array.from(new Set(this._customFields.map((item: BuilderField) => item.dbTypeName.toString()))).filter(
      item => !!item,
    );
    this.ref.detectChanges();
  }

  private initBasicFields() {
    this.basicFields = this._formBuilderHelper.basicFields;
    this.initTypeAheads();
  }

  private initCustomFields() {
    this.customFields = this._model;
    this.initTypeAheads();
  }

  fieldsByType(type, basic = false) {
    const fields = (basic ? this._basicFields : this._customFields).filter(
      (field: BuilderField) => (field.designCategory || field.dbTypeName || field.fieldType) === type,
    );
    // If there is no fields by type, remove it.
    if (!fields || !fields.length) {
      this.customControlTypes = this.customControlTypes.filter(_type => _type !== type);
      return [];
    }
    return fields;
  }

  sort(sorting: { label: string; sortable: boolean; active: boolean; asc: boolean }) {
    if (!sorting.sortable) {
      return;
    }
    sorting.active = true;
    sorting.asc = !sorting.asc;
    this.ngAfterViewInit();
  }

  updateSortings() {
    this.initSortings();
    this.ngAfterViewInit();
  }

  resetSortings() {
    this._filter = '';
    this.updateSortings();
  }

  private initSortings() {
    const sortings = this._formBuilderHelper.defaultSortings;
    this.sortings = Object.keys(sortings)
      .filter(key => sortings[key].sortable === true)
      .map(key => Object.assign({ key }, sortings[key]));
  }

  private initTypeAheads() {
    const typeaheads = [];
    if (this._customFields) {
      typeaheads.push(...this._customFields.map((field: BuilderField) => field.name));
    }
    if (this._basicFields) {
      typeaheads.push(...this._basicFields.map((field: BuilderField) => field.name));
    }
    this.typeaheads = typeaheads;
    this.ref.detectChanges();
  }

  private reloadTemplates() {
    this._downloadedTemplates = {};
    Object.keys(this._templatesChangelog).forEach(type => {
      if (this._templatesChangelog[type]) {
        this.renderTemplates(type, true);
      }
    });
  }
}
